/*
  The live stats ticker in the app bar (Robert, 2026-10-20: "cool stats across the top here
  that keep updating. like Most graded TCG, Sports … Whats hot that sort of thing").

  A slow, endless horizontal scroll of selectHeaderStats' items (lib/headerStats.ts), like a
  market ticker. Every source polls, so a figure changes in place as grading goes on.

  FIXED DARK, like the rest of the bar (AppBar's D14 note): every colour is pcg-onAccent or
  goldChrome, both the same in light and dark. Never `text-white`, which remaps to the page's
  foreground token and would go near-black on paper.

  Motion:
  - The scroll is CSS (index.css `.pcg-ticker`), so it runs on the compositor and stops when
    the tab is hidden. It pauses on hover and while anything inside has focus.
  - The pause button covers WCAG 2.2.2 (moving content needs a way to stop it), and it is
    the keyboard's way to read a chip without it sliding away.
  - Under prefers-reduced-motion it does not slide. It shows one stat at a time and fades
    to the next every few seconds (docs/specs/pop-report-batch3.md D8, D9). It used to stop
    dead instead, and that is what Robert saw on 2026-10-21 ("the top header information is
    static"): Windows' "Animation effects: off" turns reduced motion on, Chrome passes it
    to the page, and the scroll, its duplicate copy and the pause button all went. The fade
    keeps the pause button and the hover/focus pause, since it still changes by itself.

  The track holds the items twice and slides by exactly half its width, so the loop is
  seamless. The second copy is `inert` (set through the DOM, as AppBar does, since React 17
  has no prop for it): out of the tab order and the accessibility tree, so a screen reader
  hears each stat once.
*/

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  CalendarDays,
  Flame,
  Gem,
  Layers,
  Medal,
  Pause,
  Play,
  Sparkles,
  Trophy,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import {
  useGetCountsQuery,
  useGetHotGradingsQuery,
  useGetLatestFlawlessQuery,
  useGetRecentGradingsQuery,
} from "services/pcgPublic";
import { useGetGamesQuery, useGetSetsQuery } from "services/pcgPublicV2";
import { selectHeaderStats } from "lib";
import type { HeaderStat } from "lib";
import { useMediaQueryMatches } from "components/useMediaQuery";

/* The home card's own intervals for the two feeds it shares (PopulationCard). */
const COUNTS_POLL_MS = 60_000;
const RECENT_POLL_MS = 120_000;
/* "Hot right now" and the Flawless feed move more slowly and the hot window is the big one. */
const SLOW_POLL_MS = 300_000;
const SETS_POLL_MS = 1_800_000;

/* Scroll speed. Slow enough to read a chip as it passes. */
const PIXELS_PER_SECOND = 38;
const FALLBACK_SECONDS = 60;

/* The reduced-motion fade: how long each stat stays, and how long it takes to swap. */
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const FADE_HOLD_MS = 4500;
const FADE_MS = 400;

const ICONS: Record<string, LucideIcon> = {
  "top-tcg": Trophy,
  month: CalendarDays,
  hot: Flame,
  "top-sport": Medal,
  flawless: Sparkles,
  "top-set": Layers,
  latest: BadgeCheck,
  pristine: Gem,
};

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pcg-goldChrome";

const StatChip = ({ stat }: { stat: HeaderStat }) => {
  const Icon = ICONS[stat.key];
  const body = (
    <>
      {Icon ? <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-pcg-goldChrome" /> : null}
      <span className="text-[11px] font-semibold uppercase tracking-wider text-pcg-onAccent/75">
        {stat.label}
      </span>
      {stat.value ? <span className="font-semibold text-pcg-onAccent">{stat.value}</span> : null}
      {stat.figure ? (
        <span className="font-bold tabular-nums text-pcg-goldChrome">{stat.figure}</span>
      ) : null}
      {stat.detail ? <span className="text-pcg-onAccent/75">{stat.detail}</span> : null}
    </>
  );
  const chip = "flex items-center gap-2 whitespace-nowrap rounded-md px-1 text-sm";
  return (
    <li className="flex shrink-0 items-center">
      {stat.to ? (
        <Link to={stat.to} className={`${chip} no-underline hover:underline decoration-pcg-goldChrome/60 underline-offset-4 ${FOCUS_RING}`}>
          {body}
        </Link>
      ) : (
        <span className={chip}>{body}</span>
      )}
    </li>
  );
};

/* A separator between chips: a small gold diamond, decorative. */
const Separator = () => (
  <li aria-hidden="true" className="flex shrink-0 items-center px-5">
    <span className="h-1.5 w-1.5 rotate-45 bg-pcg-goldChrome/70" />
  </li>
);

const StatList = ({ stats, copy }: { stats: HeaderStat[]; copy: boolean }) => {
  const ref = useRef<HTMLUListElement>(null);
  useLayoutEffect(() => {
    if (copy) {
      ref.current?.setAttribute("inert", "");
    }
  }, [copy]);
  return (
    <ul
      ref={ref}
      aria-hidden={copy ? "true" : undefined}
      className={`m-0 flex shrink-0 list-none items-center p-0 ${copy ? "motion-reduce:hidden" : ""}`}
    >
      {stats.map(stat => (
        <StatChipWithSeparator key={stat.key} stat={stat} />
      ))}
    </ul>
  );
};

/* Each chip carries its trailing separator, so both copies end in one and the seam matches. */
const StatChipWithSeparator = ({ stat }: { stat: HeaderStat }) => (
  <>
    <StatChip stat={stat} />
    <Separator />
  </>
);

/* The ticker's items from every live source. RTK Query shares each request with its other callers. */
export const useHeaderStats = (): HeaderStat[] => {
  const games = useGetGamesQuery();
  const sets = useGetSetsQuery({ offset: 0, limit: 5 }, { pollingInterval: SETS_POLL_MS });
  const counts = useGetCountsQuery(undefined, { pollingInterval: COUNTS_POLL_MS });
  const recent = useGetRecentGradingsQuery(undefined, { pollingInterval: RECENT_POLL_MS });
  const hot = useGetHotGradingsQuery(undefined, { pollingInterval: SLOW_POLL_MS });
  const flawless = useGetLatestFlawlessQuery(undefined, { pollingInterval: SLOW_POLL_MS });

  return useMemo(
    () =>
      selectHeaderStats({
        games: games.data,
        sets: sets.data?.data,
        counts: counts.data,
        recent: recent.data?.data,
        hotWindow: hot.data?.data,
        flawless: flawless.data?.data,
        now: new Date(),
      }),
    [games.data, sets.data, counts.data, recent.data, hot.data, flawless.data]
  );
};

interface HeaderStatsTickerProps {
  stats: HeaderStat[];
  className?: string;
}

const PauseButton = ({ paused, onToggle, className = "" }: { paused: boolean; onToggle: () => void; className?: string }) => {
  const pauseLabel = paused ? "Play the live stats" : "Pause the live stats";
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={pauseLabel}
      title={pauseLabel}
      aria-pressed={paused}
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-pcg-onAccent/70 hover:bg-[rgba(255,255,255,0.12)] hover:text-pcg-onAccent ${FOCUS_RING} ${className}`}
    >
      {paused ? <Play aria-hidden="true" className="h-3.5 w-3.5" /> : <Pause aria-hidden="true" className="h-3.5 w-3.5" />}
    </button>
  );
};

/*
  The reduced-motion ticker: one stat at a time, faded to the next. Opacity only, nothing
  slides. Holds still while hovered, while anything inside has focus, and while paused.
*/
const FadeTicker = ({ stats, className = "" }: HeaderStatsTickerProps) => {
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(true);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const holding = paused || hovered || focused;
  const count = stats.length;

  useEffect(() => {
    /* First, so a count that drops to one mid-swap still brings the chip back. */
    if (!shown) {
      /* Mid-swap: finish it even if a hold started, so the bar never sits blank. */
      const timer = window.setTimeout(() => {
        setIndex(current => (count > 0 ? (current + 1) % count : 0));
        setShown(true);
      }, FADE_MS);
      return () => window.clearTimeout(timer);
    }
    if (holding || count < 2) {
      return undefined;
    }
    const timer = window.setTimeout(() => setShown(false), FADE_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [shown, holding, count, index]);

  if (count === 0) {
    return null;
  }
  const stat = stats[index % count];

  return (
    <section
      aria-label="Live PCG stats"
      className={`pcg-ticker min-w-0 items-center gap-2 ${className}`}
      data-mode="fade"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocused(false);
        }
      }}
    >
      {count > 1 ? <PauseButton paused={paused} onToggle={() => setPaused(current => !current)} /> : null}
      <div className="pcg-ticker-fade min-w-0 flex-1">
        <ul
          className={`m-0 flex list-none items-center p-0 transition-opacity ease-in-out ${shown ? "opacity-100" : "opacity-0"}`}
          style={{ transitionDuration: `${FADE_MS}ms` }}
        >
          <StatChip key={stat.key} stat={stat} />
        </ul>
      </div>
    </section>
  );
};

const ScrollTicker = ({ stats, className = "" }: HeaderStatsTickerProps) => {
  const [paused, setPaused] = useState(false);
  const [seconds, setSeconds] = useState(FALLBACK_SECONDS);
  const firstCopy = useRef<HTMLDivElement>(null);

  /*
    The duration follows the width of one copy, so the speed stays the same however many
    stats there are. Rounded to 5s so a poll that changes a figure by a digit does not
    restart the animation at a different point.
  */
  useEffect(() => {
    const node = firstCopy.current;
    if (!node) {
      return undefined;
    }
    const measure = () => {
      const width = node.getBoundingClientRect().width;
      if (width > 0) {
        setSeconds(Math.max(20, Math.round(width / PIXELS_PER_SECOND / 5) * 5));
      }
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [stats.length]);

  if (stats.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Live PCG stats"
      className={`pcg-ticker min-w-0 items-center gap-2 ${className}`}
      data-paused={paused ? "true" : "false"}
    >
      <PauseButton paused={paused} onToggle={() => setPaused(current => !current)} className="motion-reduce:hidden" />
      <div className="pcg-ticker-viewport min-w-0 flex-1">
        <div className="pcg-ticker-track flex w-max" style={{ animationDuration: `${seconds}s` }}>
          <div ref={firstCopy} className="flex shrink-0">
            <StatList stats={stats} copy={false} />
          </div>
          <StatList stats={stats} copy />
        </div>
      </div>
    </section>
  );
};

/* Scrolls, or under reduced motion fades from stat to stat; it follows the setting live. */
const HeaderStatsTicker = (props: HeaderStatsTickerProps) => {
  const reduced = useMediaQueryMatches(REDUCED_MOTION_QUERY);
  return reduced ? <FadeTicker {...props} /> : <ScrollTicker {...props} />;
};

export default HeaderStatsTicker;
